import i18n from '@/utils/i18n';
import React from 'react';
import { AccountStatusPill } from './account-status-pill';
import {
  AnalyticsAlertType,
  Locations_AccountRelationshipFieldsFragment,
} from '@/types';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { formatOrderDeliveryTime } from '@/utils/locations';
import { Text, View } from 'react-native';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';

interface Props {
  alertTypes: AnalyticsAlertType[];
  chat: Locations_AccountRelationshipFieldsFragment;
}

export const AccountStatus: React.FC<Props> = ({ alertTypes, chat }) => {
  const [styles] = useStyles();
  const { supplierEmployeeRole } = useAuthenticatedUserContext();
  const supplierUser = Boolean(supplierEmployeeRole);
  const { chatRestaurantMap, restaurantUUIDToRelationshipMap } =
    useUserContext();

  const restaurant =
    restaurantUUIDToRelationshipMap[chatRestaurantMap[chat.uuid]];

  const lastPlacedOrder = chat.placed_orders[0];
  const unplacedOrder = chat.unplaced_orders[0];

  return (
    <View style={styles.accountStatusContainer}>
      <View style={styles.header}>
        {restaurant.chatStatus === ChatStatus.ACTIVE ? (
          <>
            <Text style={[TYPOGRAPHY.small, FONTS.primaryBold, styles.title]}>
              {i18n.t('active')}
            </Text>
            <Text style={[TYPOGRAPHY.small]}>
              {lastPlacedOrder && (supplierUser || !unplacedOrder)
                ? formatOrderDeliveryTime(lastPlacedOrder)
                : null}
            </Text>
          </>
        ) : restaurant.chatStatus === ChatStatus.NEW ? (
          <Text
            style={[
              TYPOGRAPHY.small,
              FONTS.primaryBold,
              styles.title,
              styles.new,
            ]}
          >
            {i18n.t('new')}
          </Text>
        ) : restaurant.chatStatus === ChatStatus.PROSPECT ? (
          <Text
            style={[
              TYPOGRAPHY.small,
              FONTS.primaryBold,
              styles.title,
              styles.prospect,
            ]}
          >
            {i18n.t('prospect')}
          </Text>
        ) : (
          <Text
            style={[
              TYPOGRAPHY.small,
              FONTS.primaryBold,
              styles.title,
              styles.inactive,
            ]}
          >
            {i18n.t('inactive')}
          </Text>
        )}
      </View>
      {alertTypes.length ? (
        <View style={styles.pillContainer}>
          {alertTypes.map((alertType, index) => {
            return <AccountStatusPill alertType={alertType} key={index} />;
          })}
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  accountStatusContainer: {
    backgroundColor: theme.colors.blueAction,
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: 8,
  },
  header: {
    flexDirection: 'row',
  },
  inactive: {
    color: theme.colors.inactiveText,
  },
  new: {
    color: theme.colors.blueAttention,
  },
  pillContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 6,
  },
  prospect: {
    color: theme.colors.caution,
  },
  title: {
    color: theme.colors.darkGreen,
    paddingRight: 8,
    textTransform: 'uppercase',
  },
}));
